// The inline checks (§11.1, §12, reference: inline annotations and footnotes), over the prose the
// reader found: an inline kind this specification or the document's extensions do not define, a
// missing required attribute or label, a typed reference to an id the document does not have or
// to one of another kind, a citation of a source the backmatter does not record, an undeclared
// parameter, a footnote without its definition. `check` is the reader's diagnostics with these and
// the workflow checks (workflow.ts), in source order — what an editor and a validator show.
// `checkWithoutWorkflows` leaves the workflow checks out, and with them the workflow registry: it is
// what the renderer's bundle reports, since a renderer is not a workflow host (§9.1) and the registry
// would put the bundle over its size cap.
import { parseAttributes } from "./attributes";
import { inlineTokens, type InlineToken } from "./inline";
import { read, type ReadOptions, type ReadResult } from "./read";
import { INLINE, INLINE_REQUIRED, KINDS, TYPED_REFERENCES, inlineAttrSpec } from "./registry";
import type { Diagnostic, DirectiveNode, MarkdownNode, Node } from "./types";
import { WORKFLOW_DIALECT, workflowDiagnostics } from "./workflow";

const RESOURCE_KINDS = new Set(Object.entries(KINDS).filter(([, k]) => k.family === "resource").map(([n]) => n));

/** What a typed reference's target is, in this document: a local id found, of the right kind or not. */
export type Target =
  | { state: "local"; id: string; kind: string; matches: boolean }
  | { state: "missing"; id: string }
  | { state: "external"; uri: string };

/** Resolve a typed reference's `ref` against the document (§11.1): `#id`, an included `#alias/id`, a named reference, a URI. */
export function resolveTarget(r: ReadResult, kind: string, ref: string): Target {
  if (ref.startsWith("#") && !ref.includes("/")) {
    const id = ref.slice(1);
    const found = r.identities.get(id);
    if (!found) return { state: "missing", id };
    const matches = kind === "ref" || found.kind === kind || (kind === "resource" && RESOURCE_KINDS.has(found.kind));
    return { state: "local", id, kind: found.kind, matches };
  }
  const named = (r.document.metadata.front.references as Record<string, unknown> | undefined)?.[ref];
  const uri = named && typeof named === "object" && typeof (named as Record<string, unknown>).uri === "string" ? String((named as Record<string, unknown>).uri) : ref;
  return { state: "external", uri };
}

/** The reader's diagnostics, the inline checks and the workflow checks, in source order. */
export function check(input: string | ReadResult, opts: ReadOptions = {}): Diagnostic[] {
  const r = typeof input === "string" ? read(input, opts) : input;
  return inSourceOrder([...r.diagnostics, ...inlineDiagnostics(r), ...workflowsDiagnostics(r)]);
}

/** The reader's diagnostics and the inline checks, in source order: `check` without the workflow checks. */
export function checkWithoutWorkflows(input: string | ReadResult, opts: ReadOptions = {}): Diagnostic[] {
  const r = typeof input === "string" ? read(input, opts) : input;
  return inSourceOrder([...r.diagnostics, ...inlineDiagnostics(r)]);
}

const inSourceOrder = (ds: Diagnostic[]) => ds.sort((a, b) => (a.range?.start ?? 0) - (b.range?.start ?? 0) || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));

const CLOSE = /^:{3,}[ \t]*$/;

/** Each `:::workflow{dialect="instruction/workflow"}` body's diagnostics (workflow.ts), in document order: its parsed
 *  YAML or JSON (the reader reports a body that doesn't parse or isn't a mapping), placed on the body's lines.
 *  Another dialect is its own. */
export function workflowsDiagnostics(r: ReadResult): Diagnostic[] {
  const out: Diagnostic[] = [];
  const front = r.document.metadata.front.extensions;
  const extensions = front && typeof front === "object" && !Array.isArray(front) ? (front as Record<string, unknown>) : undefined;
  const visit = (nodes: Node[]) => {
    for (const n of nodes) {
      if (n.type !== "directive") continue;
      if (n.kind === "workflow" && n.attrs.dialect === WORKFLOW_DIALECT && n.value && n.value.data !== undefined && n.range) out.push(...workflowDiagnostics(n.value.data, { ...bodyLines(r, n), extensions }));
      if (n.children) visit(n.children);
    }
  };
  visit(r.document.body);
  return out;
}

/** A container's body lines (between its opener and its closing fence, which an unclosed one lacks) and its first. */
function bodyLines(r: ReadResult, n: DirectiveNode): { lines: { text: string; n: number }[]; at: number } {
  const { start, end } = n.range!;
  const last = end > start && CLOSE.test(r.lines[end - 1]) ? end - 1 : end;
  const lines = r.lines.slice(start, last).map((text, i) => ({ text, n: start + 1 + i }));
  return { lines, at: lines.length ? lines[0].n : start };
}

export function inlineDiagnostics(r: ReadResult): Diagnostic[] {
  const out: Diagnostic[] = [];
  const extensions = (r.document.metadata.front.extensions ?? {}) as Record<string, unknown>;
  const sources = new Set((Array.isArray(r.document.metadata.back.sources) ? r.document.metadata.back.sources : []).map((s) => (s && typeof s === "object" ? (s as Record<string, unknown>).id : undefined)).filter((x): x is string => typeof x === "string"));
  for (const node of r.prose) {
    for (const t of inlineTokens(node.source)) {
      const line = lineOf(node, t);
      const diag = (code: string, severity: Diagnostic["severity"], message: string) => out.push({ code, severity, message, range: { start: line, end: line } });
      if (t.type === "param") {
        if (!r.parameters.has(t.name)) diag("parameter-optional", "warning", `\${${t.name}} is not a declared parameter, so it stays as written`);
        continue;
      }
      if (t.type === "footnote") {
        if (!r.footnotes.has(t.label)) diag("footnote-missing", "warning", `the footnote [^${t.label}] has no definition, so it stays as text`);
        continue;
      }
      if (t.type !== "directive") continue;
      const where = `:${t.kind}[…]`;
      if (t.kind.includes(":")) {
        const alias = t.kind.split(":")[0];
        if (!extensions[alias]) diag("extension-unavailable", "error", `${where}: the extension alias "${alias}" is not declared in the front matter's extensions`);
        continue;
      }
      if (!(t.kind in INLINE)) { diag("unknown-kind", "error", `${where} is not an inline kind of this specification; it is kept as written`); continue; }
      const attrs = t.attributes === null ? {} : parseAttributes(t.attributes, (k) => inlineAttrSpec(t.kind, k)).attrs;
      if (t.attributes !== null) for (const p of parseAttributes(t.attributes, (k) => inlineAttrSpec(t.kind, k)).problems) diag("invalid-attributes", "error", `${where}: ${p}`);
      const need = INLINE_REQUIRED[t.kind];
      if (need && (attrs[need] === undefined || attrs[need] === "")) diag("invalid-attributes", "error", `${where} needs ${need}=…`);
      if (!t.label.trim()) diag("invalid-attributes", "error", `${where}: its label is required, the text a reader sees`);
      const ref = typeof attrs.ref === "string" ? attrs.ref : "";
      if (!ref) continue;
      if (t.kind === "cite") {
        if (!sources.has(ref)) diag("reference-optional", "warning", `${where}: the backmatter's sources record no "${ref}"`);
        continue;
      }
      if (!TYPED_REFERENCES.has(t.kind) && t.kind !== "ref") continue;
      const target = resolveTarget(r, t.kind, ref);
      if (target.state === "missing") diag("reference-optional", "warning", `${where}: nothing in this document has the id "${target.id}"`);
      else if (target.state === "local" && !target.matches) diag("reference-kind", "error", `${where} names #${target.id}, which is a ${target.kind}, not a ${t.kind}`);
    }
  }
  return out;
}

/** The source line a token stands on: its node's first line plus the line breaks before it. */
function lineOf(node: MarkdownNode, t: InlineToken): number {
  let n = node.range?.start ?? 1;
  for (let i = 0; i < t.start; i++) if (node.source.charCodeAt(i) === 10) n++;
  return n;
}
